import { Config } from '../../video-data/config';
import { ErrorReporter } from '../../libs/error-reporter';
import type { Asset, AssetType } from '../asset/BaseAsset';
import type { AssetGroup } from '../asset/AssetGroup';
import type { Scene } from '../scene/Scene';

export type SerializedStickerAsset = {
    assetType: AssetType;
    data: Record<string, unknown>;
    children?: SerializedStickerAsset[];
};

function getAssetTypeForCopy(asset: Asset): AssetType {
    if (Config.isAssetGroup(asset)) {
        return Config.GROUP_ASSET_TYPE;
    }
    if (Config.isTextAsset(asset)) {
        return Config.TEXT_ASSET_TYPE;
    }
    if (Config.isFancyCaptionAsset(asset)) {
        return Config.FANCY_CAPTION_ASSET_TYPE;
    }
    if (Config.isMediaAsset(asset)) {
        return Config.MEDIA_ASSET_TYPE;
    }
    if (Config.isIconAsset(asset)) {
        return Config.ICON_ASSET_TYPE;
    }
    if (Config.isChartAsset(asset)) {
        return Config.CHART_ASSET_TYPE;
    }
    if (Config.isColorAsset(asset)) {
        return Config.COLOR_ASSET_TYPE;
    }
    if (Config.isRectangleAsset(asset)) {
        return Config.RECTANGLE_ASSET_TYPE;
    }
    if (Config.isCircleAsset(asset)) {
        return Config.CIRCLE_ASSET_TYPE;
    }
    if (Config.isGradientAsset(asset)) {
        return Config.GRADIENT_ASSET_TYPE;
    }
    ErrorReporter.setContext('getAssetTypeForCopy', { assetName: asset.name });
    throw new Error('copyAssetToContainerAsSticker: unsupported asset type');
}

export function serializeAssetAsSticker(sourceAsset: Asset): SerializedStickerAsset {
    if (Config.isAssetGroup(sourceAsset)) {
        return {
            assetType: Config.GROUP_ASSET_TYPE,
            data: sourceAsset.save(),
            children: sourceAsset
                .getAssets({ deep: false })
                .map((child) => serializeAssetAsSticker(child)),
        };
    }

    return {
        assetType: getAssetTypeForCopy(sourceAsset),
        data: sourceAsset.save(),
    };
}

export function buildStickerFromSerializedData(
    serialized: SerializedStickerAsset,
    targetContainer: Scene | AssetGroup,
): Asset {
    if (serialized.assetType === Config.GROUP_ASSET_TYPE) {
        const newGroup = targetContainer.addSticker(Config.GROUP_ASSET_TYPE);
        (serialized.children ?? []).forEach((child) => {
            buildStickerFromSerializedData(child, newGroup);
        });
        newGroup.loadAsSticker(serialized.data);
        newGroup.generateNewId();
        return newGroup;
    }

    const newAsset = targetContainer.addSticker(serialized.assetType);
    newAsset.loadAsSticker(serialized.data);
    newAsset.generateNewId();
    return newAsset;
}

export function copyAssetToContainerAsSticker(
    sourceAsset: Asset,
    targetContainer: Scene | AssetGroup,
): Asset {
    return buildStickerFromSerializedData(serializeAssetAsSticker(sourceAsset), targetContainer);
}
